import { VFXEffectConfig, VFXPrimitive } from '../../VFXEffectRegistry';
import { slashArc, glintScatter } from './burstBuilders';

// =============================================================================
// ASSASSIN / THIEF EFFECTS
// =============================================================================

/**
 * Haste aura - cyan speed lines for haste buff
 */
const HASTE_AURA: VFXEffectConfig = {
  id: 'haste',
  name: 'Haste Aura',
  category: 'aura',
  description: 'Cyan speed lines for haste movement buff',
  followTarget: 'player',
  primitives: [
    // Pulsing cyan ellipse shell
    {
      type: 'ellipse',
      width: 60,
      height: 70,
      color: 0x00FFFF,
      alpha: 0.12,
      depth: 50,
      stroke: { color: 0x66FFFF, width: 2 },
      tween: {
        rotation: Math.PI * 0.1,
        scale: 1.06,
        duration: 120,
        ease: 'Quad.easeInOut',
        yoyo: true,
        repeat: -1
      }
    },
    // Rising sparkle particles
    {
      type: 'circle',
      radius: 4,
      color: 0x00FFFF,
      alpha: 0.6,
      depth: 51,
      tween: {
        alpha: 0,
        y: -15,
        scale: 1.5,
        duration: 600,
        ease: 'Power2'
      },
      scatter: { count: 6, radius: 45 }
    },
    // Speed streak dashes (horizontal lines sweeping past)
    {
      type: 'rect',
      width: 14,
      height: 1,
      color: 0x88FFFF,
      alpha: 0.7,
      depth: 52,
      tween: {
        x: 45,
        alpha: 0,
        scaleX: 3,
        duration: 350,
        repeat: -1,
        delay: 80
      },
      scatter: { count: 5, radius: 30 }
    }
  ]
};

/**
 * Dark Sight Aura - Character becomes translucent with shadow particles
 */
const DARK_SIGHT_AURA: VFXEffectConfig = {
  id: 'dark_sight',
  name: 'Dark Sight Aura',
  category: 'aura',
  description: 'Purple stealth aura for dark sight invisibility',
  followTarget: 'player',
  duration: 20000,
  primitives: [
    // Entity override: make character translucent and blue-tinted
    {
      type: 'entity_override',
      entityAlpha: 0.3,
      entityTint: 0x4444FF,
      tween: { duration: 20000 }
    },
    // Outer shadow shell (dark purple, pulsing)
    {
      type: 'ellipse',
      width: 65,
      height: 75,
      color: 0x663399,
      alpha: 0.15,
      depth: 50,
      stroke: { color: 0x9966CC, width: 2 },
      tween: {
        rotation: Math.PI * 0.05,
        alpha: 0.08,
        scale: 1.04,
        duration: 400,
        ease: 'Quad.easeInOut',
        yoyo: true,
        repeat: -1
      }
    },
    // Rising shadow wisps (dark particles floating upward)
    {
      type: 'circle',
      radius: 5,
      color: 0x330066,
      alpha: 0.5,
      depth: 51,
      tween: {
        y: -30,
        alpha: 0,
        scale: 0.3,
        duration: 900,
        repeat: -1
      },
      scatter: { count: 4, radius: 30 }
    },
    // Smoke particles
    {
      type: 'particles',
      particleEffectId: 'smoke',
      particleCount: 15
    }
  ]
};

/**
 * Blink out - purple implosion at origin (character vanishes)
 */
const BLINK_OUT: VFXEffectConfig = {
  id: 'blink_out',
  name: 'Blink Out',
  category: 'burst',
  description: 'Purple implosion effect for blink origin point',
  primitives: [
    // Outer ring collapsing inward
    {
      type: 'circle',
      radius: 30,
      color: 0x8800FF,
      alpha: 0.5,
      depth: 94,
      stroke: { color: 0xAA44FF, width: 2 },
      tween: {
        scale: 0,
        alpha: 0,
        duration: 200,
        ease: 'Quad.easeIn'
      }
    },
    // Vertical silhouette collapse
    {
      type: 'ellipse',
      width: 40,
      height: 80,
      color: 0x8800FF,
      alpha: 0.6,
      depth: 95,
      tween: {
        scaleX: 0,
        alpha: 0,
        duration: 200,
        ease: 'Quad.easeOut'
      }
    },
    // Shadow fragments scattering inward
    {
      type: 'circle',
      radius: 4,
      color: 0x6600CC,
      alpha: 0.8,
      depth: 96,
      tween: {
        alpha: 0,
        scale: 0.2,
        duration: 250,
        ease: 'Power2'
      },
      scatter: { count: 6, radius: 35 }
    }
  ]
};

/**
 * Blink in - cyan flash explosion at destination (character appears)
 */
const BLINK_IN: VFXEffectConfig = {
  id: 'blink_in',
  name: 'Blink In',
  category: 'burst',
  description: 'Cyan flash effect for blink destination point',
  primitives: [
    // Bright arrival flash (expanding)
    {
      type: 'circle',
      radius: 15,
      color: 0x00FFFF,
      alpha: 0.9,
      depth: 96,
      tween: {
        scale: 2.5,
        alpha: 0,
        duration: 200,
        ease: 'Power1'
      }
    },
    // Vertical beam (character materializing)
    {
      type: 'ellipse',
      width: 10,
      height: 80,
      color: 0x00FFFF,
      alpha: 0.8,
      depth: 95,
      tween: {
        scaleX: 4,
        alpha: 0,
        duration: 250,
        ease: 'Quad.easeOut'
      }
    },
    // Arrival sparks (scattered outward)
    {
      type: 'star',
      points: 4,
      innerRadius: 2,
      outerRadius: 6,
      color: 0xFFFFFF,
      alpha: 0.8,
      depth: 97,
      tween: {
        rotation: Math.PI * 0.25,
        alpha: 0,
        duration: 300,
        ease: 'Power2'
      },
      scatter: { count: 5, radius: 25 }
    }
  ]
};

/**
 * Lucky Seven Orbit - Throwing stars and cards orbiting the caster
 */
const LUCKY_SEVEN_ORBIT: VFXEffectConfig = {
  id: 'lucky_seven_orbit',
  name: 'Lucky Seven Orbit',
  category: 'orbit',
  description: 'Seven projectiles (stars and cards) orbiting the caster',
  primitives: Array.from({ length: 7 }, (_, i) => {
    const isStar = i < 4;
    return {
      type: isStar ? 'star' : 'rect',
      // Star params
      points: 5,
      innerRadius: 8,
      outerRadius: 16,
      // Rect params
      width: 16,
      height: 22,
      color: isStar ? 0xFFD700 : 0xFFF6D0, // plan 29 F9 (lane BY): the TOML gold, not grey
      alpha: 1,
      depth: 100,
      stroke: { color: isStar ? 0xB8860B : 0x8A6A1A, width: 2 },
      tween: {
        duration: 800,
        repeat: -1,
        yoyo: true,
        scale: 1.1
      },
      orbit: {
        radius: 60,
        speed: 1.2,
        startAngle: (i / 7) * Math.PI * 2,
        orientToPath: true
      }
    } as VFXPrimitive;
  })
};

/**
 * Shadow Strike - dark slash with afterimage
 */
const SHADOW_STRIKE: VFXEffectConfig = {
  id: 'shadow_strike',
  name: 'Shadow Strike',
  category: 'burst',
  description: 'Dark slash with purple afterimage for backstab attacks',
  primitives: [
    // Dark slash arc
    {
      type: 'ellipse',
      width: 55,
      height: 8,
      color: 0x6600CC,
      alpha: 0.9,
      depth: 95,
      tween: {
        rotation: Math.PI * 0.6,
        alpha: 0,
        scaleX: 1.5,
        scaleY: 0.4,
        duration: 150,
        ease: 'Power2'
      }
    },
    // Purple afterimage burst
    {
      type: 'circle',
      radius: 20,
      color: 0x8800FF,
      alpha: 0.5,
      depth: 94,
      tween: {
        alpha: 0,
        scale: 2,
        duration: 250,
        ease: 'Power2'
      }
    },
    // Shadow fragments
    {
      type: 'circle',
      radius: 3,
      color: 0x440088,
      alpha: 0.7,
      depth: 96,
      tween: {
        alpha: 0,
        scale: 0.3,
        duration: 300,
        ease: 'Power2'
      },
      scatter: { count: 5, radius: 25 }
    }
  ]
};

/**
 * Smoke bomb - expanding dark smoke cloud
 */
const SMOKE_BOMB: VFXEffectConfig = {
  id: 'smoke_bomb',
  name: 'Smoke Bomb',
  category: 'zone',
  description: 'Expanding dark smoke cloud for smoke bomb ability',
  // World-anchored summon zone (SmokeBombAbility.ts, 4s gameplay duration).
  // Was the ice_wall leak class: looping, no cap. The cap also bounds the
  // warlock curse casts that borrow this def as their cast puff.
  duration: 4000,
  primitives: [
    { type: 'circle', radius: 70, color: 0x333333, alpha: 0.4, depth: 14,
      tween: { scale: 1.3, alpha: 0.15, duration: 1500, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' } },
    { type: 'circle', radius: 40, color: 0x555555, alpha: 0.3, depth: 15,
      tween: { scale: 1.2, alpha: 0.1, rotation: Math.PI * 0.1, duration: 1000, yoyo: true, repeat: -1 } },
    { type: 'circle', radius: 5, color: 0x888888, alpha: 0.5, depth: 16,
      tween: { y: -25, alpha: 0, scale: 1.5, duration: 700, repeat: -1 }, scatter: { count: 6, radius: 40 } },
    { type: 'circle', radius: 3, color: 0x222222, alpha: 0.6, depth: 17,
      tween: { x: 20, alpha: 0, scale: 0.5, duration: 900, repeat: -1 }, scatter: { count: 4, radius: 35 } }
  ]
};

/**
 * Fan of Knives burst - spinning blade burst for fan of knives ability
 */
const FAN_OF_KNIVES_BURST: VFXEffectConfig = {
  id: 'fan_of_knives_burst',
  name: 'Fan of Knives',
  category: 'burst',
  description: 'Spinning blade burst for fan of knives ability',
  primitives: [
    // Blade streaks radiating outward — brightened for visibility against dark backgrounds
    { type: 'ellipse', width: 28, height: 8, color: 0xDDDDDD, alpha: 0.95, depth: 95,
      stroke: { color: 0xFFFFFF, width: 1 },
      tween: { rotation: Math.PI, alpha: 0, duration: 300, ease: 'Power2' },
      scatter: { count: 8, radius: 55 } },
    // Central release flash — brighter
    { type: 'circle', radius: 20, color: 0xEEEEEE, alpha: 0.8, depth: 94,
      tween: { alpha: 0, scale: 2, duration: 250, ease: 'Power2' } },
    // Steel spark tips — larger
    { type: 'star', points: 4, innerRadius: 3, outerRadius: 8, color: 0xFFFFFF, alpha: 0.9, depth: 96,
      tween: { rotation: Math.PI * 0.25, alpha: 0, duration: 250, ease: 'Power2' },
      scatter: { count: 6, radius: 45 } }
  ]
};

/**
 * Eviscerate slash - deep cutting multi-slash for eviscerate finisher
 */
const EVISCERATE_SLASH: VFXEffectConfig = {
  id: 'eviscerate_slash',
  name: 'Eviscerate Slash',
  category: 'burst',
  description: 'Deep cutting multi-slash for eviscerate finisher',
  screenShake: { intensity: 6, duration: 200 },
  primitives: [
    // Three rapid slash arcs at different angles
    { type: 'ellipse', width: 55, height: 8, color: 0xCC0000, alpha: 0.9, depth: 95,
      tween: { rotation: Math.PI * 0.4, alpha: 0, scaleX: 1.5, scaleY: 0.4, duration: 150, ease: 'Power2' } },
    { type: 'ellipse', width: 50, height: 6, color: 0xFF0000, alpha: 0.8, depth: 96,
      tween: { rotation: -Math.PI * 0.3, alpha: 0, scaleX: 1.3, scaleY: 0.3, duration: 180, ease: 'Power2' } },
    { type: 'ellipse', width: 45, height: 5, color: 0xFF3333, alpha: 0.7, depth: 94,
      tween: { rotation: Math.PI * 0.6, alpha: 0, scaleX: 1.4, scaleY: 0.5, duration: 200, ease: 'Power2' } },
    // Blood burst at center
    { type: 'circle', radius: 12, color: 0xCC0000, alpha: 0.6, depth: 93,
      tween: { alpha: 0, scale: 2, duration: 300, ease: 'Power2' } },
    // Crimson fragments
    { type: 'circle', radius: 3, color: 0x880000, alpha: 0.8, depth: 97,
      tween: { alpha: 0, scale: 0.3, duration: 350, ease: 'Power2' },
      scatter: { count: 5, radius: 25 } }
  ]
};

/**
 * Trick Link - golden buff aura for tricks_of_the_trade
 * Persistent golden aura with rising sparkles — stays for the buff duration
 */
const TRICK_LINK: VFXEffectConfig = {
  id: 'trick_link',
  name: 'Trick Link',
  category: 'aura',
  description: 'Golden buff aura with rising sparkles for tricks of the trade',
  followTarget: 'player',
  primitives: [
    // Golden aura shell (persistent, pulsing) — below player, no glow (keep color)
    { type: 'ellipse', width: 60, height: 70, color: 0xFFD700, alpha: 0.5, depth: 90,
      stroke: { color: 0xFFEE44, width: 3 },
      tween: { scale: 1.08, alpha: 0.65, duration: 500, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' } },
    // Rising golden sparkles (looping) — above player for visibility
    { type: 'circle', radius: 6, color: 0xFFEE44, alpha: 0.8, depth: 110,
      tween: { y: -35, alpha: 0, scale: 1.5, duration: 800, repeat: -1, ease: 'Power2' },
      scatter: { count: 6, radius: 30 } },
    // Golden ring pulse (looping) — above player
    { type: 'ellipse', width: 45, height: 45, color: 0xFFCC00, alpha: 0.5, depth: 105,
      stroke: { color: 0xFFD700, width: 2 },
      tween: { scale: 1.5, alpha: 0, duration: 600, repeat: -1, ease: 'Power2' } },
  ]
};

/**
 * Leap Shadow - bright purple shadow trail at leap origin for death_from_above
 */
const LEAP_SHADOW: VFXEffectConfig = {
  id: 'leap_shadow',
  name: 'Leap Shadow',
  category: 'burst',
  description: 'Bright purple shadow afterimage at leap origin for death from above',
  primitives: [
    // Bright purple shadow silhouette
    { type: 'ellipse', width: 50, height: 60, color: 0x8800FF, alpha: 0.7, depth: 95,
      tween: { alpha: 0, scale: 1.5, duration: 500, ease: 'Power2' } },
    // Bright purple particles rising
    { type: 'circle', radius: 6, color: 0xAA44FF, alpha: 0.8, depth: 96,
      tween: { y: -50, alpha: 0, scale: 0.5, duration: 500, ease: 'Power2' },
      scatter: { count: 8, radius: 30 } },
    // Bright purple ring at origin
    { type: 'ellipse', width: 45, height: 20, color: 0x6600CC, alpha: 0.7, depth: 94,
      stroke: { color: 0xAA44FF, width: 2 },
      tween: { scaleX: 2, scaleY: 2, alpha: 0, duration: 400, ease: 'Power2' } },
  ]
};

/**
 * Ground Shockwave - expanding ring + dust for death_from_above landing
 */
const GROUND_SHOCKWAVE: VFXEffectConfig = {
  id: 'ground_shockwave',
  name: 'Ground Shockwave',
  category: 'burst',
  description: 'Expanding ground ring + dust burst for death from above landing',
  screenShake: { intensity: 10, duration: 300 },
  primitives: [
    // Expanding shockwave ring
    { type: 'ellipse', width: 30, height: 15, color: 0xDDAA44, alpha: 0.8, depth: 95,
      stroke: { color: 0xFFCC66, width: 3 },
      tween: { scaleX: 4, scaleY: 4, alpha: 0, duration: 400, ease: 'Power2' } },
    // Second expanding ring (delayed)
    { type: 'ellipse', width: 20, height: 10, color: 0xFFDD88, alpha: 0.6, depth: 94,
      tween: { scaleX: 5, scaleY: 5, alpha: 0, duration: 500, ease: 'Power2', delay: 80 } },
    // Dust particles
    { type: 'circle', radius: 8, color: 0xCCAA77, alpha: 0.6, depth: 93,
      tween: { y: -25, alpha: 0, scale: 1.5, duration: 500, ease: 'Power2' },
      scatter: { count: 10, radius: 50 } },
    // Impact flash
    { type: 'circle', radius: 20, color: 0xFFEECC, alpha: 0.7, depth: 96,
      tween: { alpha: 0, scale: 2, duration: 200, ease: 'Power2' } },
  ]
};

/**
 * Shadow Burst - dark purple shadow aura for shadow_dance
 * Persistent shadow aura with swirling tendrils — stays for the buff duration
 */
const SHADOW_BURST: VFXEffectConfig = {
  id: 'shadow_burst',
  name: 'Shadow Burst',
  category: 'aura',
  description: 'Dark purple shadow aura with swirling tendrils for shadow dance',
  followTarget: 'player',
  primitives: [
    // Dark purple aura shell (persistent, pulsing) — below player, no glow
    { type: 'ellipse', width: 60, height: 70, color: 0x6600CC, alpha: 0.5, depth: 90,
      stroke: { color: 0xAA44FF, width: 3 },
      tween: { scale: 1.08, alpha: 0.65, duration: 500, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' } },
    // Shadow tendrils (looping rotation) — above player
    { type: 'ellipse', width: 35, height: 6, color: 0x8800FF, alpha: 0.7, depth: 110,
      tween: { rotation: Math.PI * 2, alpha: 0.4, duration: 800, repeat: -1, ease: 'Sine.easeInOut' },
      scatter: { count: 4, radius: 25 } },
    // Purple sparkle particles (looping rise) — above player
    { type: 'circle', radius: 5, color: 0xAA44FF, alpha: 0.8, depth: 105,
      tween: { y: -30, alpha: 0, scale: 1.5, duration: 700, repeat: -1, ease: 'Power2' },
      scatter: { count: 6, radius: 35 } },
  ]
};

/**
 * Poison Burst - toxic green splash for envenom
 */
const POISON_BURST: VFXEffectConfig = {
  id: 'poison_burst',
  name: 'Poison Burst',
  category: 'burst',
  description: 'Toxic green splash burst for envenom finisher',
  primitives: [
    // Main poison splash
    { type: 'ellipse', width: 50, height: 35, color: 0x00DD22, alpha: 0.75, depth: 95,
      tween: { scaleX: 2, scaleY: 1.5, alpha: 0, duration: 400, ease: 'Power2' } },
    // Poison droplets
    { type: 'circle', radius: 5, color: 0x00FF44, alpha: 0.85, depth: 96,
      tween: { y: -30, alpha: 0, scale: 1.5, duration: 500, ease: 'Power2' },
      scatter: { count: 8, radius: 35 } },
    // Toxic cloud
    { type: 'circle', radius: 25, color: 0x44CC00, alpha: 0.4, depth: 93,
      tween: { alpha: 0, scale: 2.5, duration: 600, ease: 'Power2' } },
    // Green splash streaks
    { type: 'ellipse', width: 30, height: 5, color: 0x66FF00, alpha: 0.7, depth: 94,
      tween: { rotation: Math.PI * 0.5, alpha: 0, scaleX: 1.5, scaleY: 0.3, duration: 300, ease: 'Power2' },
      scatter: { count: 5, radius: 25 } },
  ]
};

/**
 * Killing Spree - persistent orbiting afterimages during the five-hit channel.
 * The channel supplies its 3s lifetime, while orbit motion remains fast and
 * readable instead of being stretched to the full override duration.
 */
const SPREE_BLUR: VFXEffectConfig = {
  id: 'spree_blur',
  name: 'Killing Spree Blur',
  category: 'burst',
  description: 'Red-violet blade afterimages orbiting the caster during Killing Spree',
  duration: 3100,
  primitives: [
    { type: 'circle', radius: 34, color: 0x330018, alpha: 0.35, depth: 92, glow: true },
    { type: 'shockwave', shockwaveInnerRadius: 25, shockwaveOuterRadius: 42,
      color: 0xFF3355, alpha: 0.65, depth: 93, glow: true,
      stroke: { color: 0xFF99AA, width: 3 } },
    ...Array.from({ length: 5 }, (_, i): VFXPrimitive => ({
      type: 'polygon',
      polygonPoints: [-25, -4, 12, -4, 27, 0, 12, 4, -25, 4, -32, 0],
      color: i % 2 === 0 ? 0xFF3355 : 0xBB55FF,
      alpha: 0.96,
      depth: 100 + i,
      stroke: { color: 0xFFFFFF, width: 1 },
      orbit: {
        radius: 68,
        speed: 2.8,
        startAngle: (i / 5) * Math.PI * 2,
        orientToPath: true,
      },
    })),
  ],
};

/** Caster-centered blade storm for Crimson Tempest's full damage window. */
const CRIMSON_SPIN: VFXEffectConfig = {
  id: 'crimson_spin',
  name: 'Crimson Tempest',
  category: 'aura',
  description: 'A wide crimson ring with seven orbiting blades around the caster',
  duration: 8100,
  primitives: [
    { type: 'circle', radius: 68, color: 0x550008, alpha: 0.28, depth: 91, glow: true },
    { type: 'shockwave', shockwaveInnerRadius: 52, shockwaveOuterRadius: 72,
      color: 0xCC0018, alpha: 0.75, depth: 93, glow: true,
      stroke: { color: 0xFF5566, width: 4 } },
    ...Array.from({ length: 7 }, (_, i): VFXPrimitive => ({
      type: 'ellipse',
      width: 46,
      height: 8,
      color: i % 2 === 0 ? 0xFF2244 : 0xAA0018,
      alpha: 0.92,
      depth: 98 + i,
      glow: true,
      stroke: { color: 0xFF99AA, width: 2 },
      orbit: {
        radius: 62,
        speed: 1.35,
        startAngle: (i / 7) * Math.PI * 2,
        orientToPath: true,
      },
    })),
  ],
};

/** Directional origin-to-destination streak for Blade Rush. */
const BLADE_RUSH_TRAIL: VFXEffectConfig = {
  id: 'blade_rush_trail',
  name: 'Blade Rush Trail',
  category: 'trail',
  description: 'Long violet blade streak showing the complete dash path',
  duration: 320,
  primitives: [
    { type: 'beam', beamLength: 300, beamWidth: 11, beamSegments: 1,
      color: 0xAA44FF, alpha: 0.85, depth: 98, glow: true,
      tween: { alpha: 0, scaleY: 0.25, duration: 280, ease: 'Power2' } },
    { type: 'line', linePoints: [{ x: 0, y: -10 }, { x: 100, y: 3 }, { x: 205, y: -6 }, { x: 300, y: 0 }],
      lineWidth: 5, lineJitter: 4, color: 0xEECCFF, alpha: 0.9, depth: 100, glow: true,
      tween: { alpha: 0, duration: 300, ease: 'Power2' } },
    { type: 'shockwave', shockwaveInnerRadius: 12, shockwaveOuterRadius: 28,
      color: 0x7733CC, alpha: 0.7, depth: 96,
      tween: { alpha: 0, scale: 1.8, duration: 260, ease: 'Power2' } },
  ],
};

/** Sharp violet-red contact beat at Blade Rush's target. */
const BLADE_RUSH: VFXEffectConfig = {
  id: 'blade_rush',
  name: 'Blade Rush Impact',
  category: 'burst',
  description: 'Crossed violet blades and a bright contact flash',
  screenShake: { intensity: 5, duration: 140 },
  primitives: [
    { type: 'arc', arcRadius: 54, arcStartAngle: -65, arcEndAngle: 55,
      color: 0xCC66FF, alpha: 0.95, depth: 101, glow: true,
      stroke: { color: 0xFFFFFF, width: 7 },
      tween: { alpha: 0, scale: 1.25, duration: 220, ease: 'Power2' } },
    { type: 'ellipse', width: 70, height: 9, color: 0xFF3355, alpha: 0.9, depth: 100, glow: true,
      tween: { rotation: Math.PI * 0.35, alpha: 0, scaleX: 1.35, duration: 220, ease: 'Power2' } },
    { type: 'ellipse', width: 62, height: 8, color: 0xAA44FF, alpha: 0.9, depth: 102, glow: true,
      tween: { rotation: -Math.PI * 0.35, alpha: 0, scaleX: 1.35, duration: 220, ease: 'Power2' } },
    { type: 'circle', radius: 18, color: 0xFFFFFF, alpha: 0.9, depth: 103, glow: true,
      tween: { alpha: 0, scale: 2, duration: 180, ease: 'Power2' } },
  ],
};

/** Target-centered spectral slash for Ghostly Strike. */
const GHOSTLY_STRIKE: VFXEffectConfig = {
  id: 'ghostly_strike',
  name: 'Ghostly Strike',
  category: 'burst',
  description: 'Pale cyan-violet slash with spectral wisps',
  primitives: [
    // Main motifs intentionally stay flat/non-bloomed for their full 150ms
    // melee window. Bloom on these shapes previously collapsed them into a
    // generic pink-white blob in screenshots.
    { type: 'arc', arcRadius: 58, arcStartAngle: -75, arcEndAngle: 50,
      color: 0x55EEFF, alpha: 1, depth: 104,
      stroke: { color: 0xEEFFFF, width: 10 } },
    { type: 'polygon', polygonPoints: [-40, -5, 20, -5, 42, 0, 20, 5, -40, 5, -48, 0],
      color: 0x7744EE, alpha: 0.95, depth: 103 },
    { type: 'circle', radius: 7, color: 0xCCFFFF, alpha: 0.8, depth: 101, glow: true,
      tween: { y: -34, alpha: 0, scale: 0.35, duration: 380, ease: 'Power2' },
      scatter: { count: 6, radius: 32 } },
    { type: 'circle', radius: 24, color: 0x553388, alpha: 0.35, depth: 96, glow: true,
      tween: { alpha: 0, scale: 1.8, duration: 360, ease: 'Power2' } },
  ],
};

/** Persistent orbiting weapon silhouettes for the Shadow Blades buff. */
const SHADOW_WEAPON_AURA: VFXEffectConfig = {
  id: 'shadow_weapon_aura',
  name: 'Shadow Weapon Aura',
  category: 'aura',
  description: 'Three unmistakable shadow blades orbiting the empowered rogue',
  duration: 15000,
  primitives: [
    { type: 'shockwave', shockwaveInnerRadius: 29, shockwaveOuterRadius: 36,
      color: 0x8833CC, alpha: 0.55, depth: 92, glow: true,
      stroke: { color: 0xCC88FF, width: 3 } },
    ...Array.from({ length: 3 }, (_, i): VFXPrimitive => ({
      type: 'polygon',
      polygonPoints: [-32, -5, 18, -5, 34, 0, 18, 5, -32, 5, -41, 0],
      color: i === 1 ? 0xDD99FF : 0x8833CC,
      alpha: 0.98,
      depth: 102 + i,
      stroke: { color: 0xF4DDFF, width: 2 },
      orbit: {
        radius: 44,
        speed: 0.9,
        startAngle: (i / 3) * Math.PI * 2,
        orientToPath: true,
      },
    })),
    { type: 'star', points: 4, innerRadius: 3, outerRadius: 9,
      color: 0xFFFFFF, alpha: 0.8, depth: 106, glow: true,
      orbit: { radius: 48, speed: -1.1, startAngle: Math.PI / 3 } },
  ],
};

/** Initial target wound for Rupture. */
const RUPTURE: VFXEffectConfig = {
  id: 'rupture',
  name: 'Rupture Wound',
  category: 'burst',
  description: 'Deep crossed crimson cuts and a blood flash on the target',
  screenShake: { intensity: 4, duration: 120 },
  primitives: [
    { type: 'line', linePoints: [{ x: -34, y: -25 }, { x: 34, y: 25 }],
      lineWidth: 8, color: 0xFF2233, alpha: 0.95, depth: 103, glow: true,
      tween: { alpha: 0, scale: 1.3, duration: 420, ease: 'Power2' } },
    { type: 'line', linePoints: [{ x: -30, y: 25 }, { x: 30, y: -25 }],
      lineWidth: 6, color: 0xAA0011, alpha: 0.9, depth: 102,
      tween: { alpha: 0, scale: 1.25, duration: 460, ease: 'Power2' } },
    { type: 'circle', radius: 20, color: 0xCC0018, alpha: 0.55, depth: 98, glow: true,
      tween: { alpha: 0, scale: 2, duration: 480, ease: 'Power2' } },
    { type: 'circle', radius: 4, color: 0x880008, alpha: 0.9, depth: 104,
      tween: { y: 24, alpha: 0, duration: 520, ease: 'Power2' },
      scatter: { count: 6, radius: 30 } },
  ],
};

/** Persistent crossed wound used by bleed debuffs on targets. */
const BLEED: VFXEffectConfig = {
  id: 'bleed',
  name: 'Bleeding Wound',
  category: 'status',
  description: 'Persistent crossed wound and blood drops attached to a bleeding target',
  duration: 10000,
  primitives: [
    { type: 'line', linePoints: [{ x: -22, y: -18 }, { x: 22, y: 18 }],
      lineWidth: 5, color: 0xEE1122, alpha: 0.82, depth: 101, glow: true },
    { type: 'line', linePoints: [{ x: -18, y: 18 }, { x: 18, y: -18 }],
      lineWidth: 4, color: 0x880008, alpha: 0.8, depth: 100 },
    { type: 'circle', radius: 5, offsetX: -12, offsetY: 25,
      color: 0xAA0011, alpha: 0.8, depth: 102, glow: true },
    { type: 'circle', radius: 4, offsetX: 6, offsetY: 31,
      color: 0xDD1122, alpha: 0.75, depth: 102, glow: true },
    { type: 'circle', radius: 3, offsetX: 18, offsetY: 22,
      color: 0x770008, alpha: 0.8, depth: 102 },
  ],
};

/**
 * Sinister strike cut — K21 art heat-map physical slice (2026-07-17).
 * The rogue read of the shared quick_slash generic: two RAZOR-thin crossing
 * arcs + a shadow-violet follow edge (rogue identity), doubled metal glints.
 * No dust, no ring, no shake — a knife, not a two-hander. Degrees vocab
 * (arcRadius/arcStartAngle/arcEndAngle) so weaponSweepTrail can layer on
 * the same angle windows later without repaint.
 */
const SINISTER_STRIKE_CUT: VFXEffectConfig = {
  id: 'sinister_strike_cut',
  name: 'Sinister Strike Cut',
  category: 'burst',
  description: 'Two razor-thin crossing steel arcs with a shadow-violet follow edge and glints',
  primitives: [
    // Razor pair — crossing, simultaneous, FAST.
    slashArc({ radius: 36, startDeg: -60, endDeg: 30, width: 3, color: 0xF2F6F8, alpha: 0.95, durationMs: 130, glow: true }),
    slashArc({ radius: 32, startDeg: 210, endDeg: 120, width: 2, color: 0xC8CED4, durationMs: 150 }),
    // Shadow-violet follow edge — the sinister tell, a beat behind the steel.
    slashArc({ radius: 40, startDeg: -55, endDeg: 25, width: 4, color: 0x8877AA, alpha: 0.5, durationMs: 180, fadeDelayMs: 60 }),
    // Doubled glints: edge flash then drifting motes.
    glintScatter({ count: 3, spread: 20, radius: 2, durationMs: 140 }),
    glintScatter({ count: 4, spread: 32, radius: 2, delayMs: 80, durationMs: 180 }),
  ],
};

const BLEED_EFFECT: VFXEffectConfig = {
  ...BLEED,
  id: 'bleed_effect',
  name: 'Crimson Tempest Bleed',
  duration: 8000,
};

// =============================================================================
// EXPORTS
// =============================================================================

export const ASSASSIN_VFX: VFXEffectConfig[] = [
  HASTE_AURA,
  DARK_SIGHT_AURA,
  BLINK_OUT,
  BLINK_IN,
  LUCKY_SEVEN_ORBIT,
  SHADOW_STRIKE,
  SMOKE_BOMB,
  FAN_OF_KNIVES_BURST,
  EVISCERATE_SLASH,
  TRICK_LINK,
  LEAP_SHADOW,
  GROUND_SHOCKWAVE,
  SHADOW_BURST,
  POISON_BURST,
  SPREE_BLUR,
  CRIMSON_SPIN,
  BLADE_RUSH_TRAIL,
  BLADE_RUSH,
  GHOSTLY_STRIKE,
  SHADOW_WEAPON_AURA,
  RUPTURE,
  BLEED,
  BLEED_EFFECT,
  SINISTER_STRIKE_CUT,
];
